import { Edit, Link2, Plus, Trash2 } from 'lucide-react';
import {
  Avatar,
  Button,
  Card,
  Chip,
  Description,
  EmptyState,
  Heading,
  Table,
} from '@heroui/react';
import type { UserMembership, UserWithMemberships } from '../../../api/users';
import {
  getMembershipRoleChipColor,
  getMembershipRoleLabel,
  getMembershipStatusChipColor,
  getMembershipStatusLabel,
  getUserDisplayName,
  getUserInitials,
} from '../utils/user-labels';

interface UserDetailPanelProps {
  user: UserWithMemberships | null;
  onAssociate: (user: UserWithMemberships) => void;
  onEditMembership: (membership: UserMembership, user: UserWithMemberships) => void;
  onDeleteMembership: (membership: UserMembership, user: UserWithMemberships) => void;
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Description>{label}</Description>
      <p className="text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

export function UserDetailPanel({
  user,
  onAssociate,
  onEditMembership,
  onDeleteMembership,
}: UserDetailPanelProps) {
  if (!user) {
    return (
      <Card className="flex min-h-[24rem] items-center justify-center">
        <Card.Content className="py-16 text-center">
          <EmptyState className="flex flex-col items-center gap-2">
            <Heading level={3}>Selecione um usuário</Heading>
            <Description>Escolha alguém na lista à esquerda para ver detalhes e associações.</Description>
          </EmptyState>
        </Card.Content>
      </Card>
    );
  }

  const displayName = getUserDisplayName(user);

  return (
    <Card className="min-h-[24rem]">
      <Card.Content className="flex flex-col gap-6 p-6">
        <div className="flex items-start gap-4 border-b border-separator pb-6">
          <Avatar size="lg" color="accent">
            {user.photoUrl ? <Avatar.Image src={user.photoUrl} alt={displayName} /> : null}
            <Avatar.Fallback>{getUserInitials(user)}</Avatar.Fallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <Heading level={2} className="truncate text-xl font-semibold">
              {displayName}
            </Heading>
            <Description className="truncate">{user.email}</Description>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <InfoRow label="CPF" value={user.documentNumber ?? '—'} />
          <InfoRow label="Telefone" value={user.phone ?? '—'} />
          <InfoRow label="Cadastro" value={formatDate(user.createdAt)} />
          <InfoRow label="Último login" value={formatDate(user.lastLoginAt)} />
          <InfoRow
            label="Associações"
            value={`${user.memberships.length} condomínio(s)`}
          />
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Heading level={3} className="text-base font-semibold">
              Associações
            </Heading>
            <Button size="sm" variant="secondary" onPress={() => onAssociate(user)}>
              <Plus size={16} />
              Associar condomínio
            </Button>
          </div>
          <Description className="text-xs text-muted">
            Moradores já vinculados aparecem no condomínio para ajuste de apartamento.
          </Description>

          {user.memberships.length === 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-dashed border-separator px-4 py-6 text-sm text-muted">
              <Link2 size={16} className="shrink-0" />
              <span>Sem associações. Use &quot;Associar condomínio&quot; para vincular este usuário.</span>
            </div>
          ) : (
            <Table aria-label={`Associações de ${displayName}`}>
              <Table.ScrollContainer>
                <Table.Content>
                  <Table.Header>
                    <Table.Column isRowHeader>Condomínio</Table.Column>
                    <Table.Column>Papel</Table.Column>
                    <Table.Column>Status</Table.Column>
                    <Table.Column className="w-24 text-right">Ações</Table.Column>
                  </Table.Header>
                  <Table.Body items={user.memberships}>
                    {(membership) => (
                      <Table.Row id={membership.id}>
                        <Table.Cell>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-foreground">
                              {membership.condominiumName}
                            </p>
                            <p className="truncate text-xs text-muted">@{membership.condominiumSlug}</p>
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={getMembershipRoleChipColor(membership.role)}
                          >
                            {getMembershipRoleLabel(membership.role)}
                          </Chip>
                        </Table.Cell>
                        <Table.Cell>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={getMembershipStatusChipColor(membership.status)}
                          >
                            {getMembershipStatusLabel(membership.status)}
                          </Chip>
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex justify-end gap-1">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="ghost"
                              aria-label="Editar associação"
                              onPress={() => onEditMembership(membership, user)}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              isIconOnly
                              size="sm"
                              variant="ghost"
                              aria-label="Remover associação"
                              onPress={() => onDeleteMembership(membership, user)}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </Button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}
